import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Form } from "react-bootstrap";
import { Button } from "bootstrap-4-react/lib/components";
import Select from "react-select";
import { toast } from "react-toastify";
import {
    prepareMarketplaceListing,
    publishMarketplaceListing,
    saveDraftMarketplaceListing,
    createMarketplaceLocation,
    getInventoryListings,
    getMarketplaceCategoryAspects,
} from "../../../store/actions/marketplaceListing.action";

// Condition options match _mapCondition in walmart-service/ebay.adapter.js.
// Keep these labels consistent with the eBay enum we forward.
const CONDITION_OPTIONS = [
    { value: "New", label: "New" },
    { value: "New Other", label: "New Other" },
    { value: "Like New", label: "Like New" },
    { value: "Manufacturer Refurbished", label: "Manufacturer Refurbished" },
    { value: "Seller Refurbished", label: "Seller Refurbished" },
    { value: "Used Excellent", label: "Used — Excellent" },
    { value: "Used Very Good", label: "Used — Very Good" },
    { value: "Used Good", label: "Used — Good" },
    { value: "Used Acceptable", label: "Used — Acceptable" },
    { value: "For Parts or Not Working", label: "For Parts or Not Working" },
];

/**
 * Helpers to bridge react-select's { value, label } object shape with the
 * adapter API which expects plain id/key strings.
 */
const toOption = (val, options) => options.find((o) => o.value === val) || null;
const optionFromIdName = (item) => (item ? { value: item.id, label: item.name } : null);
const optionFromCategory = (c) => (c ? { value: c.categoryId, label: c.categoryPath || c.categoryName } : null);
const optionFromLocation = (l) => (l ? { value: l.key, label: l.name } : null);

const EBAY_MARKETPLACE = "ebay";
// Primary: durable Seller Hub path (main www.ebay.com domain, not the Akamai-fronted
// bizpolicy.ebay.com subdomain which has had transient DNS failures). The seller
// navigates: Account Settings → Business Policies in the left nav.
const POLICY_HUB_URL = "https://www.ebay.com/sh/settings/account";
// Direct deep-link to the opt-in page on bizpolicy.ebay.com — used as a secondary
// link since it goes straight to "Opt in" but the host has been intermittent.
const POLICY_OPTIN_URL = "https://www.bizpolicy.ebay.com/businesspolicy/policyoptin";

/**
 * Render a single dynamic aspect field. eBay aspects come back with
 *   { name, type, mode, cardinality, required, values, suggestedValues }
 * and we translate that to the right input.
 */
function AspectField({ aspect, value, onChange }) {
    const isEnum = aspect.mode === "SELECTION_ONLY" && aspect.values?.length > 0;
    const isMulti = aspect.cardinality === "MULTI";
    const isNumber = aspect.type === "NUMBER";

    if (isEnum && !isMulti) {
        const options = aspect.values.map((v) => ({ value: v, label: v }));
        return (
            <Select
                options={options}
                value={value ? { value, label: value } : null}
                onChange={(opt) => onChange(opt ? opt.value : "")}
                placeholder={`-- Select ${aspect.name} --`}
                classNamePrefix="list"
                isClearable
            />
        );
    }

    if (isMulti) {
        const suggestedHint = aspect.suggestedValues?.length
            ? ` (e.g., ${aspect.suggestedValues.slice(0, 3).join(", ")})`
            : "";
        const placeholder = `Enter values separated by commas${suggestedHint}`;
        return (
            <Form.Control
                type="text"
                placeholder={placeholder}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }

    return (
        <Form.Control
            type={isNumber ? "number" : "text"}
            placeholder={`Enter ${aspect.name}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

AspectField.propTypes = {
    aspect: PropTypes.shape({
        name: PropTypes.string.isRequired,
        type: PropTypes.string,
        mode: PropTypes.string,
        cardinality: PropTypes.string,
        required: PropTypes.bool,
        values: PropTypes.arrayOf(PropTypes.string),
        suggestedValues: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};
AspectField.defaultProps = { value: "" };

/**
 * Pick the right submit-button label for the four (submitting × existingListing) states.
 */
function publishButtonLabel(submitting, isUpdate) {
    if (submitting && isUpdate) return "Updating…";
    if (submitting) return "Publishing…";
    if (isUpdate) return "Update Listing";
    return "Publish to eBay";
}

/**
 * Convert form values back to eBay's expected `{ aspectName: [values] }` shape.
 * eBay rejects bare strings — every value is wrapped in an array even for SINGLE cardinality.
 */
function buildAspectsPayload(aspectFormState, allAspects) {
    const out = {};
    for (const aspect of allAspects) {
        const raw = aspectFormState[aspect.name];
        if (raw === undefined || raw === null || raw === "") continue;
        if (aspect.cardinality === "MULTI") {
            const arr = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
            if (arr.length) out[aspect.name] = arr;
        } else {
            out[aspect.name] = [String(raw)];
        }
    }
    return out;
}

// ============================================================================
// loadPrereqs() helpers — extracted to keep the cognitive complexity of the
// caller below the 15-point threshold. Each is pure (no React state); the
// caller owns the setters.
// ============================================================================

/**
 * Map a prepare-response error to one of the known categorized branches the
 * caller renders distinctly (opted-out / missing policies / missing locations /
 * generic load error). Returns null if the response is healthy.
 */
function classifyPrepareError(body) {
    if (body?.success === false && body?.error?.code === "EBAY_BUSINESS_POLICIES_OPTED_OUT") {
        return { kind: "OPTED_OUT", message: body.error.message || "Seller has opted out of eBay business policies." };
    }
    if (body?.success === false && body?.error?.code === "MISSING_POLICIES") {
        return { kind: "MISSING_POLICIES", message: body.error.message || "Missing eBay business policies." };
    }
    if (body?.success === false && body?.error?.code === "MISSING_LOCATIONS") {
        return { kind: "MISSING_LOCATIONS", message: body.error.message || "No eBay inventory locations." };
    }
    if (body?.success !== true) {
        return { kind: "LOAD_ERROR", message: body?.error?.message || "Failed to load eBay listing fields." };
    }
    return null;
}

/**
 * Parse a draft payload string defensively. Returns null on malformed JSON
 * so the caller can fall through to product defaults instead of crashing.
 */
function parseDraftPayload(payloadString) {
    if (!payloadString) return null;
    try {
        return JSON.parse(payloadString);
    } catch (parseErr) {
        // eslint-disable-next-line no-console
        console.warn("EbayStep: ignoring malformed draftPayload", parseErr);
        return null;
    }
}

/**
 * Coerce a numeric/string field from (draft → product → fallback). Avoids the
 * nested ternaries that Sonar flags when this logic is inlined.
 */
function pickNumericString(draftVal, productVal, fallback) {
    if (draftVal !== undefined && draftVal !== null) return String(draftVal);
    if (productVal) return String(productVal);
    return fallback;
}

/**
 * Flatten draft aspects from publish shape ({ name: [values] }) to the form
 * state shape ({ name: string }, where MULTI values are comma-joined). Returns
 * an empty object when the input isn't a usable aspects bag.
 */
function flattenDraftAspects(aspects) {
    const flat = {};
    if (!aspects || typeof aspects !== "object") return flat;
    for (const [name, arr] of Object.entries(aspects)) {
        if (Array.isArray(arr)) {
            flat[name] = arr.length > 1 ? arr.join(", ") : (arr[0] || "");
        } else if (arr !== undefined && arr !== null) {
            flat[name] = String(arr);
        }
    }
    return flat;
}

/**
 * Aspect values specifically (Brand, Type, etc.) — separated from the rest of
 * buildInitialFormValues so the fallback tier is easy to follow:
 *   1. Saved draft payload's aspects (the user's last input in this form) —
 *      wins whenever a draft exists AT ALL, even one with intentionally empty
 *      aspects. "Save Draft" skips required-field validation (see
 *      handleSaveDraft), so a seller can explicitly clear Brand/Type and save
 *      that; we must never second-guess an explicit draft by silently
 *      refilling it from eBay.
 *   2. Whatever's actually on the live eBay listing right now
 *      (existingListing.currentAspects — see marketplaceListing.service.js
 *      fetchCurrentAspects). Reached ONLY when there's no draft record at
 *      all — a listing published without ever going through "Save Draft"
 *      here (listed manually on eBay, or published before this fallback
 *      existed) has real Brand/Type values on eBay that this form would
 *      otherwise show as blank required fields.
 *   3. Empty — same as before this fallback existed.
 */
function buildInitialAspectValues(draft, existingListing) {
    if (draft) return flattenDraftAspects(draft.aspects);
    return flattenDraftAspects(existingListing?.currentAspects);
}

/**
 * Build the initial form values for the eBay listing form. Pre-fill priority:
 *   1. Saved draft payload (the user's last input on this product+marketplace)
 *   2. Adapter product snapshot (.NET's ProductById response)
 *   3. Top-level `product` prop (the row from the inventory grid)
 *   4. Hard-coded defaults
 * (aspectValues specifically has its own fallback tier — see buildInitialAspectValues.)
 */
function buildInitialFormValues({ draft, productSnapshot, productProp, prepareData }) {
    const p = productSnapshot || {};
    return {
        title: draft?.title || p.productName || productProp.productName || "",
        description: draft?.description || p.productDescription || productProp.productDescription || "",
        price: pickNumericString(draft?.price, p.sellingPrice, ""),
        quantity: pickNumericString(draft?.quantity, p.quantity, "1"),
        condition: draft?.condition || p.condition || p.productCondition || "New",
        categoryId: draft?.categoryId || prepareData.suggestedCategory?.categoryId || "",
        fulfillmentPolicyId: draft?.fulfillmentPolicyId || prepareData.policies?.fulfillment?.[0]?.id || "",
        paymentPolicyId: draft?.paymentPolicyId || prepareData.policies?.payment?.[0]?.id || "",
        returnPolicyId: draft?.returnPolicyId || prepareData.policies?.return?.[0]?.id || "",
        merchantLocationKey: draft?.merchantLocationKey || prepareData.locations?.[0]?.key || "",
        aspectValues: buildInitialAspectValues(draft, prepareData.existingListing),
    };
}

/**
 * Resolve the existingListing object. Prefers the prepare response (which
 * walmart-service surfaces with the joined draft fields); falls back to the
 * direct .NET GetByInventoryId call if not present.
 */
function pickExistingListing(existingFromPrepare, existingResp) {
    if (existingFromPrepare) return existingFromPrepare;
    const listings = existingResp?.data?.EntityList || [];
    return listings.find((l) => String(l.marketplaceName).toLowerCase() === "ebay") || null;
}

/**
 * Apply a categorized prepare-response error to the appropriate state setter.
 * Returns true when an error was handled (caller should bail). Lives at module
 * scope so the EbayStep component body stays below Sonar's cognitive complexity
 * threshold — the setters are injected as a map.
 */
function applyPrepareError(error, setters) {
    if (!error) return false;
    const setter = setters[error.kind];
    if (setter) setter(error.message);
    return true;
}

/**
 * Render a static status banner for one of the "we can't show the form yet"
 * conditions: opted-out / missing-policies / load-error / post-publish-success.
 *
 * Returns the JSX to display, or null if EbayStep should proceed to its main
 * form render. Consolidating the four short status returns at module scope
 * keeps EbayStep's body below Sonar's cognitive-complexity threshold.
 *
 * Order matters: success (publishResult) wins over errors so a successful
 * publish during a noisy session doesn't get masked by stale error state.
 * The dynamic onboarding paths (loading spinner / missing-locations form) stay
 * inline in EbayStep — they need too many local closures to be worth lifting.
 */
function renderEbayStatusBanner({
    policiesOptedOut, missingPolicies, loadError, publishResult, existingListing, onSkip,
}) {
    if (publishResult) {
        return (
            <div className="alert alert-success">
                <strong>
                    {existingListing ? "Listing updated on eBay!" : "Listing published to eBay!"}
                </strong>
                {publishResult.listingUrl && (
                    <p className="mb-0 mt-2">
                        <a href={publishResult.listingUrl} target="_blank" rel="noreferrer">
                            View on eBay →
                        </a>
                    </p>
                )}
            </div>
        );
    }
    if (policiesOptedOut) {
        return (
            <div className="alert alert-warning">
                <strong>Opt in to eBay business policies first.</strong>
                <p className="mb-2 mt-2">{policiesOptedOut}</p>
                <p className="mb-2">
                    eBay&apos;s newer business-policies model isn&apos;t enabled on this account.
                    Without it, listings can&apos;t reference fulfillment / payment / return policies.
                </p>
                <p className="mb-2">
                    {"Open the "}
                    <a href={POLICY_HUB_URL} target="_blank" rel="noreferrer">
                        eBay Seller Hub
                    </a>
                    {" → "}
                    <em>Account Settings</em>
                    {" → "}
                    <em>Business Policies</em>
                    {" in the left nav, and click "}
                    <em>Opt in</em>
                    {"."}
                </p>
                <p className="mb-2" style={{ fontSize: 12, color: "#666" }}>
                    {"Alternate direct link: "}
                    <a href={POLICY_OPTIN_URL} target="_blank" rel="noreferrer">
                        bizpolicy.ebay.com — opt in
                    </a>
                    {" (may be intermittently unavailable; try Seller Hub above if it fails)."}
                </p>
                <p className="mb-2">
                    After opting in, create at least one fulfillment, payment, and return policy. Then come back and try again.
                </p>
                <Button className="cus-primary-transparent-btn mt-2" onClick={onSkip}>
                    Skip eBay
                </Button>
            </div>
        );
    }
    if (missingPolicies) {
        return (
            <div className="alert alert-warning">
                <strong>Set up your eBay business policies first.</strong>
                <p className="mb-2 mt-2">{missingPolicies}</p>
                <p className="mb-2">
                    {"Open the "}
                    <a href={POLICY_HUB_URL} target="_blank" rel="noreferrer">
                        eBay Seller Hub
                    </a>
                    {" → "}
                    <em>Account Settings</em>
                    {" → "}
                    <em>Business Policies</em>
                    {" to create a fulfillment, payment, and return policy. Then come back and try again."}
                </p>
                <Button className="cus-primary-transparent-btn mt-2" onClick={onSkip}>
                    Skip eBay
                </Button>
            </div>
        );
    }
    if (loadError) {
        return (
            <div className="alert alert-danger">
                {loadError}
                <div className="mt-2">
                    <Button className="cus-primary-transparent-btn" onClick={onSkip}>Skip eBay</Button>
                </div>
            </div>
        );
    }
    return null;
}

/**
 * Status line under the Category field — reflects the in-flight
 * refetch/error state from handleCategoryChange, or the default hint when
 * idle. Extracted (rather than a nested ternary inline) per Sonar S3358.
 */
function renderCategoryAspectsStatus({ aspectsRefreshing, aspectsRefreshError }) {
    if (aspectsRefreshing) {
        return (
            <p className="text-muted" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
                <i className="fa fa-spinner fa-spin mr-1" aria-hidden="true" />
                {" "}
                Updating required fields for this category…
            </p>
        );
    }
    if (aspectsRefreshError) {
        return (
            <p className="error dialogError" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
                {aspectsRefreshError}
            </p>
        );
    }
    return (
        <p className="text-muted" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
            Required eBay fields below depend on this category and update automatically when you change it.
        </p>
    );
}

/**
 * eBay step in the MarketplacePushWizard.
 *
 * Self-contained: loads its own prereqs on mount, manages its own form state,
 * publishes when the user clicks Publish. Reports back to the wizard via
 * onPublishComplete(result) so the wizard can mark the step status and decide
 * whether to auto-advance.
 *
 * `draftOnly` mode: when true, the Publish button is hidden and only Save Draft
 * remains. Used by MarketplaceDraftsSection on the Inventory Product page, where
 * the intent is to pre-populate the listing form without ever calling the
 * marketplace API from that surface. Everything else (load, pre-fill, aspect
 * rendering, location onboarding) is identical to the wizard-mode behavior.
 */
const EbayStep = forwardRef(function EbayStep({ product, marketplaceId, onPublishComplete, onDraftSaved, onSkip, draftOnly, hidePublishButton }, ref) {
    const [loading, setLoading] = useState(false);
    const [prereqs, setPrereqs] = useState(null);
    const [missingPolicies, setMissingPolicies] = useState(null);
    const [policiesOptedOut, setPoliciesOptedOut] = useState(null);
    const [missingLocations, setMissingLocations] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [existingListing, setExistingListing] = useState(null);
    // True when the form was pre-filled from a previously-saved draft on .NET.
    // Drives a banner above the form so the user knows where the values came from.
    const [draftLoaded, setDraftLoaded] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState("");
    const [quantity, setQuantity] = useState("");
    const [condition, setCondition] = useState("New");
    const [categoryId, setCategoryId] = useState("");
    const [fulfillmentPolicyId, setFulfillmentPolicyId] = useState("");
    const [paymentPolicyId, setPaymentPolicyId] = useState("");
    const [returnPolicyId, setReturnPolicyId] = useState("");
    const [merchantLocationKey, setMerchantLocationKey] = useState("");
    const [aspectValues, setAspectValues] = useState({});
    // Required/recommended/optional aspects are category-specific on eBay —
    // these track the in-flight refetch triggered when the user picks a
    // different category (see handleCategoryChange).
    const [aspectsRefreshing, setAspectsRefreshing] = useState(false);
    const [aspectsRefreshError, setAspectsRefreshError] = useState(null);
    const [showRecommended, setShowRecommended] = useState(false);
    const [showOptional, setShowOptional] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [publishResult, setPublishResult] = useState(null);

    // Inline location-create form state (shown when missingLocations is set)
    const [locName, setLocName] = useState("Main Warehouse");
    const [locAddressLine1, setLocAddressLine1] = useState("");
    const [locAddressLine2, setLocAddressLine2] = useState("");
    const [locCity, setLocCity] = useState("");
    const [locState, setLocState] = useState("");
    const [locPostalCode, setLocPostalCode] = useState("");
    const [locCountry, setLocCountry] = useState("US");
    const [locSubmitting, setLocSubmitting] = useState(false);

    const productId = product?.id || product?.Id;

    /**
     * Fetch eBay prereqs + existing listing. Extracted as a callable so the
     * location-create form can re-trigger it after successful registration.
     *
     * If a `cancelledRef` ({ current: boolean }) is supplied, mid-flight
     * results are dropped when current becomes true — used by useEffect's
     * cleanup to abandon stale loads on unmount. Callers that don't need
     * cancellation can omit the argument entirely.
     */
    /**
     * Push the result of buildInitialFormValues() into the corresponding setters.
     * Centralizes the setter sweep so loadPrereqs stays linear.
     */
    const applyFormValues = (values) => {
        setTitle(values.title);
        setDescription(values.description);
        setPrice(values.price);
        setQuantity(values.quantity);
        setCondition(values.condition);
        setCategoryId(values.categoryId);
        setFulfillmentPolicyId(values.fulfillmentPolicyId);
        setPaymentPolicyId(values.paymentPolicyId);
        setReturnPolicyId(values.returnPolicyId);
        setMerchantLocationKey(values.merchantLocationKey);
        setAspectValues(values.aspectValues);
    };

    const loadPrereqs = async (cancelledRef) => {
        const isCancelled = () => cancelledRef?.current === true;

        setLoading(true);
        setLoadError(null);
        setMissingPolicies(null);
        setPoliciesOptedOut(null);
        setMissingLocations(null);
        setPrereqs(null);
        setPublishResult(null);
        try {
            const [prepResp, existingResp] = await Promise.all([
                prepareMarketplaceListing(EBAY_MARKETPLACE, {
                    companySellerId: product.companySellerId,
                    inventoryProductId: productId,
                    marketplaceRegion: "EBAY_US",
                }),
                getInventoryListings({ inventoryProductId: productId }).catch(() => ({ data: { EntityList: [] } })),
            ]);
            if (isCancelled()) return;

            const body = prepResp?.data;
            const prepareError = classifyPrepareError(body);
            const errorHandled = applyPrepareError(prepareError, {
                OPTED_OUT: setPoliciesOptedOut,
                MISSING_POLICIES: setMissingPolicies,
                MISSING_LOCATIONS: setMissingLocations,
                LOAD_ERROR: setLoadError,
            });
            if (errorHandled) return;

            const data = body.data || {};
            console.log("loadPrereqs - Product data from API:", {
                product: data.product,
                images: data.product?.images,
                imageCount: data.product?.images?.length || 0,
            });
            setPrereqs(data);

            // Pre-fill from a saved draft if one exists; otherwise fall back to product
            // defaults + adapter suggestions. The prepare response already includes
            // existingListing for either a Draft row or a Published row; we honor
            // draftPayload regardless of status so a user can edit and re-push a
            // previously published listing too.
            const existingFromPrepare = data.existingListing;
            const draft = parseDraftPayload(existingFromPrepare?.draftPayload);

            applyFormValues(buildInitialFormValues({
                draft,
                productSnapshot: data.product,
                productProp: product,
                prepareData: data,
            }));
            setDraftLoaded(!!draft);

            const existingEbay = pickExistingListing(existingFromPrepare, existingResp);
            if (existingEbay) setExistingListing(existingEbay);
        } catch (err) {
            if (!isCancelled()) setLoadError(err?.response?.data?.error?.message || err.message || "Failed to load eBay listing fields.");
        } finally {
            if (!isCancelled()) setLoading(false);
        }
    };

    useEffect(() => {
        if (!productId) return undefined;
        const cancelledRef = { current: false };
        loadPrereqs(cancelledRef);
        return () => { cancelledRef.current = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    /**
     * One-time onboarding: create the seller's eBay inventory location.
     * eBay's Inventory API requires a merchantLocationKey on every offer but
     * doesn't expose location management in Seller Hub for most sellers —
     * so we collect the address here and call the location/create endpoint.
     * On success, re-run loadPrereqs() so the listing form replaces this one.
     */
    const handleCreateLocation = async () => {
        if (!locAddressLine1.trim()) { toast.error("Street address is required."); return; }
        if (!locCity.trim()) { toast.error("City is required."); return; }
        if (!locState.trim()) { toast.error("State / province is required."); return; }
        if (!locPostalCode.trim()) { toast.error("Postal code is required."); return; }
        if (!locCountry.trim()) { toast.error("Country is required."); return; }

        setLocSubmitting(true);
        try {
            const resp = await createMarketplaceLocation(EBAY_MARKETPLACE, {
                companySellerId: product.companySellerId,
                locationKey: "wh-1",
                name: locName.trim() || "Main Warehouse",
                address: {
                    addressLine1: locAddressLine1.trim(),
                    addressLine2: locAddressLine2.trim() || undefined,
                    city: locCity.trim(),
                    stateOrProvince: locState.trim(),
                    postalCode: locPostalCode.trim(),
                    country: locCountry.trim().toUpperCase(),
                },
            });

            const body = resp?.data;
            if (body?.success !== true || body?.data?.success !== true) {
                const msg = body?.data?.lastError || body?.error?.message || "Failed to register eBay location.";
                toast.error(msg);
                return;
            }
            toast.success("eBay location registered.");
            // Re-load prereqs — the form below this step will now render.
            // User-triggered reload — no cancellation needed (component is mounted by definition)
            await loadPrereqs();
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || err.message || "Failed to register eBay location.");
        } finally {
            setLocSubmitting(false);
        }
    };

    const allAspects = useMemo(() => {
        if (!prereqs) return [];
        return [
            ...(prereqs.requiredFields || []),
            ...(prereqs.recommendedFields || []),
            ...(prereqs.optionalFields || []),
        ];
    }, [prereqs]);

    const handleAspectChange = (name, value) => {
        setAspectValues((prev) => ({ ...prev, [name]: value }));
    };

    /**
     * Required/recommended/optional aspects are category-specific on eBay —
     * a category picked from the dropdown after the form already loaded
     * previously left the Required/Recommended/Optional sections showing the
     * ORIGINAL suggested category's fields, since they're only ever fetched
     * once (in loadPrereqs). The publish payload carried the newly selected
     * categoryId while the collected aspects still matched the old one, so
     * eBay would reject the publish for a required aspect the UI never
     * displayed at all. Refetch on every category change instead of
     * requiring the modal to be closed and reopened.
     */
    const handleCategoryChange = async (opt) => {
        const newCategoryId = opt ? opt.value : "";
        setCategoryId(newCategoryId);
        setAspectsRefreshError(null);
        if (!newCategoryId) return;

        setAspectsRefreshing(true);
        try {
            const resp = await getMarketplaceCategoryAspects(EBAY_MARKETPLACE, {
                companySellerId: product.companySellerId,
                categoryId: newCategoryId,
                marketplaceRegion: prereqs?.marketplaceMeta?.marketplaceRegion,
            });
            const data = resp?.data?.data;
            if (resp?.data?.success && data) {
                setPrereqs((prev) => ({
                    ...prev,
                    requiredFields: data.requiredFields || [],
                    recommendedFields: data.recommendedFields || [],
                    optionalFields: data.optionalFields || [],
                }));
            } else {
                setAspectsRefreshError(resp?.data?.error?.message || "Failed to load fields for this category.");
            }
        } catch (err) {
            setAspectsRefreshError(err?.response?.data?.error?.message || err.message || "Failed to load fields for this category.");
        } finally {
            setAspectsRefreshing(false);
        }
    };

    /** Resolve all available image URLs for this product. Used by validate() and the publish payload. */
    const resolveImageUrls = () => {
        const p = prereqs?.product || {};
        const fromImages = (p.images || [])
            .filter((img) => !img.isDeleted)
            .map((img) => img.imageUrl)
            .filter(Boolean);
        if (fromImages.length) return fromImages;
        if (product.imageUrl) return [product.imageUrl];
        if (p.imageUrl) return [p.imageUrl];
        if (p.thumbnailUrl) return [p.thumbnailUrl];
        if (product.thumbnailUrl) return [product.thumbnailUrl];
        return [];
    };

    /** Get image URLs or null if none found (for safer payload building). */
    const getImageUrlsForPayload = () => {
        const urls = resolveImageUrls();
        return urls.length > 0 ? urls : null;
    };

    /** Returns null if all required aspect fields are filled, or an error message. */
    const validateRequiredAspects = () => {
        for (const a of (prereqs?.requiredFields || [])) {
            const v = aspectValues[a.name];
            if (v === undefined || v === null || String(v).trim() === "") {
                return `${a.name} is required.`;
            }
        }
        return null;
    };

    const validate = () => {
        const trimmedTitle = title.trim();
        // Declarative rule list: [check, errorMessage]. First failing rule wins.
        // Keeps validate()'s cognitive complexity below the Sonar threshold by
        // replacing 10+ sequential if-blocks with one loop over the rules.
        const rules = [
            [!trimmedTitle, "Title is required."],
            [trimmedTitle.length > 80, `Title is ${trimmedTitle.length} characters. eBay's limit is 80 — please shorten it.`],
            [!description?.trim(), "Description is required."],
            [!price || Number.parseFloat(price) <= 0, "Enter a valid price."],
            [!quantity || Number.parseInt(quantity, 10) <= 0, "Enter a valid quantity."],
            [!categoryId, "Select a category."],
            [!fulfillmentPolicyId, "Select a fulfillment policy."],
            [!paymentPolicyId, "Select a payment policy."],
            [!returnPolicyId, "Select a return policy."],
            [!merchantLocationKey, "Select a warehouse location."],
            [resolveImageUrls().length === 0, "Please add a product image in Inventory before publishing — eBay requires at least one image per listing."],
        ];
        for (const [failed, message] of rules) {
            if (failed) { toast.error(message); return false; }
        }
        const aspectError = validateRequiredAspects();
        if (aspectError) { toast.error(aspectError); return false; }
        return true;
    };

    /**
     * Build the eBay payload from current form state. Used by both publish and
     * save-draft so the two paths persist the same shape — that's what makes
     * "save now, publish later" lossless: prepare round-trips the same JSON.
     *
     * Draft saves call this with the form possibly half-filled. Numeric coercions
     * intentionally tolerate empty strings (NaN survives JSON.stringify as null);
     * publish-time validation catches missing required fields earlier via validate().
     */
    const buildPayload = () => {
        const p = prereqs?.product || {};
        const sku = p.eBaySku || p.productSku || product.productSku;
        const imageUrls = getImageUrlsForPayload();

        return {
            sku,
            title: title.trim(),
            description,
            condition,
            price: price === "" ? null : Number.parseFloat(price),
            currency: prereqs?.marketplaceMeta?.currency || "USD",
            quantity: quantity === "" ? null : Number.parseInt(quantity, 10),
            categoryId,
            fulfillmentPolicyId,
            paymentPolicyId,
            returnPolicyId,
            merchantLocationKey,
            imageUrls: imageUrls || [],  // Ensure it's at least an empty array, not null
            brand: p.brand || null,
            mpn: p.mpn || null,
            upc: p.productUpc || p.upc || null,
            ean: p.ean || null,
            aspects: buildAspectsPayload(aspectValues, allAspects),
            existingListing: existingListing ? {
                externalListingId: existingListing.externalListingId,
                externalOfferId: existingListing.externalOfferId,
                listingStatus: existingListing.listingStatus,
                listingUrl: existingListing.listingUrl,
            } : null,
        };
    };

    /**
     * Build the schemaVersion tag stored alongside the draft. Lets a future
     * load detect spec drift (eBay rev-bumps category aspect lists). We bake
     * in the category ID since aspects vary per category, plus a date stamp
     * from prereqs if eBay surfaced one.
     */
    const buildSchemaVersion = () => {
        const cat = categoryId || "unknown";
        const stamp = prereqs?.marketplaceMeta?.aspectsLastModified || new Date().toISOString().slice(0, 10);
        return `ebay:${cat}:${stamp}`;
    };

    const handleSaveDraft = async () => {
        // Drafts skip publish-validation. The user can save partial work — they'll
        // only hit validate() when they come back and click Publish. The one thing
        // we still require is a SKU, because the payload key requires it and the
        // backend will reject the call with MISSING_SKU otherwise.
        const payload = buildPayload();
        if (!payload.sku) {
            toast.error("This product has no SKU. Add one in Inventory before saving a draft.");
            return;
        }

        setSavingDraft(true);
        try {
            const resp = await saveDraftMarketplaceListing(EBAY_MARKETPLACE, {
                companySellerId: product.companySellerId,
                inventoryProductId: productId,
                marketplaceId,
                payload,
                schemaVersion: buildSchemaVersion(),
            });
            const body = resp?.data;
            if (body?.success !== true) {
                const msg = body?.error?.message || "Failed to save eBay draft.";
                toast.error(msg);
                return;
            }
            toast.success("eBay draft saved.");
            // Reflect persisted state so subsequent Publish uses existing-listing routing
            // if the draft row picked up status from a prior publish (it won't here, but
            // the wizard's onDraftSaved hook drives step status either way).
            const row = body.data?.listing || null;
            if (row) setExistingListing(row);
            setDraftLoaded(true);
            onDraftSaved?.({ success: true, listing: row });
        } catch (err) {
            const msg = err?.response?.data?.error?.message || err.message || "Failed to save eBay draft.";
            toast.error(msg);
        } finally {
            setSavingDraft(false);
        }
    };

    const handlePublish = async () => {
        if (!validate()) return;

        setSubmitting(true);
        try {
            const payload = buildPayload();

            const resp = await publishMarketplaceListing(EBAY_MARKETPLACE, {
                companySellerId: product.companySellerId,
                inventoryProductId: productId,
                marketplaceId,
                payload,
            });

            const body = resp?.data;
            if (body?.success !== true) {
                toast.error(body?.error?.message || "Failed to publish to eBay.");
                onPublishComplete?.({ success: false, lastError: body?.error?.message });
                return;
            }
            const result = body.data || {};
            if (result.success) {
                setPublishResult(result);
                toast.success(existingListing ? "Listing updated on eBay." : "Listing published to eBay.");
                onPublishComplete?.(result);
            } else {
                toast.error(result.lastError || "eBay rejected the listing.");
                onPublishComplete?.(result);
            }
        } catch (err) {
            const msg = err?.response?.data?.error?.message || err.message || "Failed to publish to eBay.";
            toast.error(msg);
            onPublishComplete?.({ success: false, lastError: msg });
        } finally {
            setSubmitting(false);
        }
    };

    // Imperative handle used by BulkEbayListingModal to collect payloads and
    // dispatch bulk-publish results back into each mounted form. No deps arg —
    // the handle re-forms every render so callers always see fresh closures.
    useImperativeHandle(ref, () => ({
        isReady: () => !loading
            && !missingPolicies && !policiesOptedOut && !missingLocations && !loadError
            && !!prereqs,
        validate: () => validate(),
        getInventoryProductId: () => productId,
        getCompanySellerId: () => product?.companySellerId,
        getMarketplaceRegion: () => prereqs?.marketplaceMeta?.marketplaceRegion || "EBAY_US",
        getCurrency: () => prereqs?.marketplaceMeta?.currency || "USD",
        getExistingListing: () => existingListing,
        buildFullPayload: () => buildPayload(),
        applyBulkResult: (result) => {
            if (result?.success) setPublishResult(result);
        },
    }));

    if (loading) {
        return (
            <div className="text-center py-5">
                <output className="spinner-border text-primary" aria-hidden="true" />
                <p className="mt-3 text-muted">Fetching listing fields from eBay…</p>
            </div>
        );
    }

    if (missingLocations) {
        return (
            <div className="modal_container_rate_card fontFix">
                <div className="alert alert-info">
                    <strong>One-time setup: register your warehouse address.</strong>
                    <p className="mb-0 mt-2" style={{ fontSize: 13 }}>
                        eBay requires a registered warehouse address before publishing listings.
                        This is a one-time setup per seller — once saved, all future eBay listings
                        will use the same location.
                    </p>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">Warehouse Name:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locName}
                            onChange={(e) => setLocName(e.target.value)}
                            placeholder="Main Warehouse"
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">Street Address:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locAddressLine1}
                            onChange={(e) => setLocAddressLine1(e.target.value)}
                            placeholder="e.g., 123 Main Street"
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">Address Line 2:</span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locAddressLine2}
                            onChange={(e) => setLocAddressLine2(e.target.value)}
                            placeholder="Apt, suite, unit (optional)"
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">City:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locCity}
                            onChange={(e) => setLocCity(e.target.value)}
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">State / Province:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locState}
                            onChange={(e) => setLocState(e.target.value)}
                            placeholder="e.g., NY (2-letter for US)"
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">Postal Code:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locPostalCode}
                            onChange={(e) => setLocPostalCode(e.target.value)}
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">Country:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locCountry}
                            onChange={(e) => setLocCountry(e.target.value)}
                            placeholder="2-letter ISO code (US, GB, DE, …)"
                            maxLength={2}
                            style={{ textTransform: "uppercase" }}
                        />
                    </div>
                </div>

                <p className="text-muted" style={{ fontSize: 12, marginTop: 12 }}>
                    Note: the address cannot be changed after creation — eBay locks it once registered.
                    Use your real shipping origin. The name can be edited later.
                </p>

                <div className="text-right" style={{ marginTop: 16 }}>
                    <Button
                        className="cus-primary-transparent-btn"
                        onClick={onSkip}
                        disabled={locSubmitting}
                    >
                        Skip eBay
                    </Button>
                    <Button
                        className="cus-seconday-bg-btn marginLeft5"
                        onClick={handleCreateLocation}
                        disabled={locSubmitting}
                    >
                        {locSubmitting ? "Registering…" : "Save Location"}
                    </Button>
                </div>
            </div>
        );
    }

    // Consolidated status-banner check (opted-out / missing-policies / load-error /
    // post-publish-success). Extracted to renderEbayStatusBanner() at module scope
    // so the component body stays below Sonar's cognitive-complexity threshold.
    const statusBanner = renderEbayStatusBanner({
        policiesOptedOut, missingPolicies, loadError, publishResult, existingListing, onSkip,
    });
    if (statusBanner) return statusBanner;

    if (!prereqs) return null;

    // Helper: resolve the currently-selected category object across both lists
    const allCategories = [
        prereqs.suggestedCategory,
        ...(prereqs.otherCategorySuggestions || []),
    ].filter(Boolean);
    const selectedCategory = allCategories.find((c) => c.categoryId === categoryId);

    return (
        <div className="modal_container_rate_card fontFix">
            <div className="row modal_input">
                <div className="col-12 mobMb-10">
                    <p className="mb-3" style={{ fontSize: "13px", color: "#666" }}>
                        <strong>{prereqs.product?.productSku || product.productSku}</strong>
                        {` — ${prereqs.product?.productName || product.productName || ""}`}
                        {existingListing && (
                            <span className="badge badge-info ml-2">Existing listing — Update mode</span>
                        )}
                    </p>
                </div>
            </div>

            {draftLoaded && (
                <div className="alert alert-warning py-2" style={{ fontSize: 13 }}>
                    <i className="fa fa-pencil-square-o mr-2" aria-hidden="true" />
                    {"Loaded from your saved draft. Make changes and click "}
                    <strong>Publish to eBay</strong>
                    {" when ready, or "}
                    <strong>Save Draft</strong>
                    {" to update without publishing."}
                </div>
            )}

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Title:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <input
                        type="text"
                        className="form-control availableBalanceInput"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={80}
                    />
                    <p
                        className={title.length > 80 ? "error dialogError" : "text-muted"}
                        style={{ fontSize: 12, marginTop: 4, marginBottom: 0 }}
                    >
                        {title.length}/80 — eBay limits titles to 80 characters.
                    </p>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Description:</span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <textarea
                        className="form-control availableBalanceInput"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">
                        Price ({prereqs.marketplaceMeta?.currency || "USD"}):<span style={{ color: "red" }}> *</span>
                    </span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control availableBalanceInput"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                    />
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Quantity:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <input
                        type="number"
                        min="1"
                        className="form-control availableBalanceInput"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                    />
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Condition:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={CONDITION_OPTIONS}
                            value={toOption(condition, CONDITION_OPTIONS)}
                            onChange={(opt) => setCondition(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select condition..."
                        />
                    </div>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Category:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={allCategories.map(optionFromCategory)}
                            value={optionFromCategory(selectedCategory)}
                            onChange={handleCategoryChange}
                            isDisabled={aspectsRefreshing}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select category..."
                        />
                    </div>
                    {renderCategoryAspectsStatus({ aspectsRefreshing, aspectsRefreshError })}
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Fulfillment Policy:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={(prereqs.policies?.fulfillment || []).map(optionFromIdName)}
                            value={optionFromIdName((prereqs.policies?.fulfillment || []).find((p) => p.id === fulfillmentPolicyId))}
                            onChange={(opt) => setFulfillmentPolicyId(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select policy..."
                        />
                    </div>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Payment Policy:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={(prereqs.policies?.payment || []).map(optionFromIdName)}
                            value={optionFromIdName((prereqs.policies?.payment || []).find((p) => p.id === paymentPolicyId))}
                            onChange={(opt) => setPaymentPolicyId(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select policy..."
                        />
                    </div>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Return Policy:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={(prereqs.policies?.return || []).map(optionFromIdName)}
                            value={optionFromIdName((prereqs.policies?.return || []).find((p) => p.id === returnPolicyId))}
                            onChange={(opt) => setReturnPolicyId(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select policy..."
                        />
                    </div>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Warehouse Location:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={(prereqs.locations || []).map(optionFromLocation)}
                            value={optionFromLocation((prereqs.locations || []).find((l) => l.key === merchantLocationKey))}
                            onChange={(opt) => setMerchantLocationKey(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select warehouse..."
                        />
                    </div>
                </div>
            </div>

            {prereqs.requiredFields?.length > 0 && (
                <fieldset className="mb-3">
                    <legend style={{ fontSize: 14, fontWeight: 600 }}>
                        Required Fields ({prereqs.requiredFields.length})
                    </legend>
                    {prereqs.requiredFields.map((aspect) => (
                        <Form.Group key={aspect.name} className="mb-2">
                            <Form.Label>
                                {aspect.name} <span style={{ color: "red" }}>*</span>
                            </Form.Label>
                            <AspectField
                                aspect={aspect}
                                value={aspectValues[aspect.name]}
                                onChange={(v) => handleAspectChange(aspect.name, v)}
                            />
                        </Form.Group>
                    ))}
                </fieldset>
            )}

            {prereqs.recommendedFields?.length > 0 && (
                <fieldset className="mb-3">
                    <legend style={{ fontSize: 14, fontWeight: 600 }}>
                        <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{ fontSize: 14, fontWeight: 600, textDecoration: "none" }}
                            onClick={() => setShowRecommended((v) => !v)}
                            aria-expanded={showRecommended}
                        >
                            <i className={`fa fa-chevron-${showRecommended ? "down" : "right"} mr-2`} />
                            {" "}
                            {`Recommended Fields (${prereqs.recommendedFields.length})`}
                        </button>
                    </legend>
                    {showRecommended && prereqs.recommendedFields.map((aspect) => (
                        <Form.Group key={aspect.name} className="mb-2">
                            <Form.Label>{aspect.name}</Form.Label>
                            <AspectField
                                aspect={aspect}
                                value={aspectValues[aspect.name]}
                                onChange={(v) => handleAspectChange(aspect.name, v)}
                            />
                        </Form.Group>
                    ))}
                </fieldset>
            )}

            {prereqs.optionalFields?.length > 0 && (
                <fieldset className="mb-3">
                    <legend style={{ fontSize: 14, fontWeight: 600 }}>
                        <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{ fontSize: 14, fontWeight: 600, textDecoration: "none" }}
                            onClick={() => setShowOptional((v) => !v)}
                            aria-expanded={showOptional}
                        >
                            <i className={`fa fa-chevron-${showOptional ? "down" : "right"} mr-2`} />
                            {" "}
                            {`Optional Fields (${prereqs.optionalFields.length})`}
                        </button>
                    </legend>
                    {showOptional && prereqs.optionalFields.map((aspect) => (
                        <Form.Group key={aspect.name} className="mb-2">
                            <Form.Label>{aspect.name}</Form.Label>
                            <AspectField
                                aspect={aspect}
                                value={aspectValues[aspect.name]}
                                onChange={(v) => handleAspectChange(aspect.name, v)}
                            />
                        </Form.Group>
                    ))}
                </fieldset>
            )}

            <p style={{ fontSize: 12, color: "#888", marginTop: 16 }}>
                {draftOnly
                    ? "Saved fields are stored on this product. Use \"Push to Marketplace\" on the Inventory page when you're ready to publish to eBay."
                    : "Publishing creates a billable listing on eBay. Insertion fees may apply depending on your store tier. Saving a draft stores your input without publishing, so you can finish later."}
            </p>

            <div className="text-right">
                <Button
                    className="cus-primary-transparent-btn mr-2"
                    onClick={handleSaveDraft}
                    disabled={submitting || savingDraft || aspectsRefreshing}
                >
                    {savingDraft ? "Saving…" : "Save Draft"}
                </Button>
                {!draftOnly && !hidePublishButton && (
                    <Button
                        className="cus-seconday-bg-btn"
                        onClick={handlePublish}
                        disabled={submitting || savingDraft || aspectsRefreshing}
                    >
                        {publishButtonLabel(submitting, !!existingListing)}
                    </Button>
                )}
            </div>
        </div>
    );
});

EbayStep.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        productSku: PropTypes.string,
        productName: PropTypes.string,
        productDescription: PropTypes.string,
        imageUrl: PropTypes.string,
        thumbnailUrl: PropTypes.string,
        companySellerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    marketplaceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onPublishComplete: PropTypes.func,
    onDraftSaved: PropTypes.func,
    onSkip: PropTypes.func,
    draftOnly: PropTypes.bool,
    hidePublishButton: PropTypes.bool,
};
EbayStep.defaultProps = { onPublishComplete: undefined, onDraftSaved: undefined, onSkip: undefined, draftOnly: false, hidePublishButton: false };

export default EbayStep;
